import { FormGroup, Select } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IEmployment, IServiceOrganizationConsumer } from '@frontend/components/interfaces/';
import { getDisplayName } from '@common/utils/getDisplayName';
import { computeSelectProps } from '@frontend/components/fields/SelectField';
import { fetchJson } from '@frontend/store/fetch';
import { getValidationErrorTranslation } from '@frontend/utils/index';
import type { ICustomControlProcedureAddEditProps } from '../../interfaces/';

export function OorkV1ProcedureSelectRequester(props: ICustomControlProcedureAddEditProps): JSX.Element {
    const [serviceConsumerEmployments, setServiceConsumerEmployments] = useState<Partial<IEmployment>[] | null>(null);

    useEffect(() => {
        loadRequesters();
    }, []);

    return (
        <FormGroup
            hasErrors={props.errors.has('requesterOrganizationId')}
            isRequired={true}
            label="Wie is de aanvrager?"
        >
            <Select
                {...computeSelectProps({
                    error: getValidationErrorTranslation(props.errors.get('requesterOrganizationId')),
                    onChange: async (value) => await onChangeRequester(value),
                    onDoValidate: async () => props.validateOne('requesterOrganizationId'),
                    options: getRequesterOptions(),
                    placeholder: 'Selecteer een aanvrager...',
                    value: getValue(),
                })}
            />
        </FormGroup>
    );

    async function loadRequesters(): Promise<void> {
        const items = (await fetchJson(`/api/serviceConsumerOrganizations?serviceId=${props.service.id}`, 'GET')).json
            .items as IServiceOrganizationConsumer[];
        const employments: Partial<IEmployment>[] = items
            .map((item) => {
                return item.serviceConsumers.map((serviceConsumer) => ({
                    ...serviceConsumer.employment,
                    organization: item.organization,
                }));
            })
            .flat();

        setServiceConsumerEmployments(employments);
        if (employments.length === 1) {
            await onChangeRequester(`${employments[0].organization?.id}_${employments[0].practitioner?.id}`);
        }
    }

    function getRequesterOptions(): {
        label: string;
        value: string;
    }[] {
        if (!serviceConsumerEmployments) {
            return [];
        }

        return serviceConsumerEmployments.map((employment) => ({
            label: getDisplayName(employment.practitioner!),
            value: `${employment.organization?.id}_${employment.practitioner?.id}`,
        }));
    }

    async function onChangeRequester(organizationAndPractitionerId: string | null) {
        await props.handleChange(
            'requesterOrganizationId',
            organizationAndPractitionerId ? organizationAndPractitionerId.split('_')[0] : null
        );
        await props.handleChange(
            'requesterPractitionerId',
            organizationAndPractitionerId ? organizationAndPractitionerId.split('_')[1] : null
        );
    }

    function getValue(): string | null {
        if (props.procedure.requesterOrganizationId && props.procedure.requesterPractitionerId) {
            return `${props.procedure.requesterOrganizationId}_${props.procedure.requesterPractitionerId}`;
        }

        return null;
    }
}
